import { useEffect, useMemo, useRef, useState } from "react";
import { BackupReportItem } from "../../api/admin";
import type {
  ReportDateRange,
  ReportRangeType,
} from "../../view/contents/history/backup.history";
import {
  getSortedRowModel,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "../common/table.pagination";
import { TableContainer } from "../../style/contents/backup/backup";
import { useForm } from "react-hook-form";
import Calendar from "react-calendar";
import type { Value } from "react-calendar/dist/shared/types";
import "react-calendar/dist/Calendar.css";
import gothicA1RegularUrl from "../../fonts/gothicA1/GothicA1-Regular.woff";
import gothicA1BoldUrl from "../../fonts/gothicA1/GothicA1-Bold.woff";

interface Props {
  data: BackupReportItem[];
  pageSize: number;
  isLoading?: boolean; // ✅ 추가
  rangeType: ReportRangeType;
  dateRange: ReportDateRange;
  onRangeSelect: (rangeType: Exclude<ReportRangeType, "custom">) => void;
  onCustomDateChange: (field: keyof ReportDateRange, value: string) => void;
}

const formatDate = (value: number | string | null | undefined) => {
  if (!value) return "-";

  let date: Date;

  // 1️⃣ 숫자 or 숫자 문자열 (timestamp)
  if (
    typeof value === "number" ||
    (typeof value === "string" && /^\d+$/.test(value))
  ) {
    date = new Date(Number(value));
  }
  // 2️⃣ 일반 날짜 문자열
  else {
    date = new Date(value);
  }

  // ❌ Invalid Date 방어
  if (isNaN(date.getTime())) return "-";

  // return date.toLocaleString("ko-KR", {
  //   year: "numeric",
  //   month: "2-digit",
  //   day: "2-digit",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   second: "2-digit",
  // });

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseDateParam = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatReportType = (value: string | null | undefined) => {
  const type = String(value || "").toLowerCase();

  if (type === "backup") return "백업";
  if (type === "download") return "다운로드";

  return value || "-";
};

const formatReportValue = (value: string | null | undefined) => value || "-";

const pdfFontFiles = {
  regular: "GothicA1-Regular.woff",
  bold: "GothicA1-Bold.woff",
};

let pdfMakeReady: Promise<
  typeof import("pdfmake/build/pdfmake").default
> | null = null;

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const fetchFontAsBase64 = async (fontUrl: string) => {
  const response = await fetch(fontUrl);

  if (!response.ok) {
    throw new Error(
      `PDF font fetch failed: ${response.status} ${response.statusText}`,
    );
  }

  const buffer = await response.arrayBuffer();

  return arrayBufferToBase64(buffer);
};

const preparePdfMakeFonts = () => {
  if (!pdfMakeReady) {
    pdfMakeReady = Promise.all([
      import("pdfmake/build/pdfmake"),
      import("pdfmake/build/vfs_fonts"),
      fetchFontAsBase64(gothicA1RegularUrl),
      fetchFontAsBase64(gothicA1BoldUrl),
    ])
      .then(([pdfMakeModule, pdfFontsModule, regularFont, boldFont]) => {
        const pdfMake = pdfMakeModule.default;
        const pdfFonts = pdfFontsModule.default;

        pdfMake.addVirtualFileSystem(pdfFonts);
        pdfMake.addVirtualFileSystem({
          [pdfFontFiles.regular]: regularFont,
          [pdfFontFiles.bold]: boldFont,
        });
        pdfMake.addFonts({
          GothicA1: {
            normal: pdfFontFiles.regular,
            bold: pdfFontFiles.bold,
            italics: pdfFontFiles.regular,
            bolditalics: pdfFontFiles.bold,
          },
        });

        return pdfMake;
      })
      .catch((err) => {
        pdfMakeReady = null;
        throw err;
      });
  }

  return pdfMakeReady;
};

const getReportTableBody = (rows: BackupReportItem[]) => {
  const body: unknown[][] = [
    [
      { text: "날짜", style: "tableHeader" },
      { text: "동작", style: "tableHeader" },
      { text: "사용자 ID", style: "tableHeader" },
      { text: "경로", style: "tableHeader" },
      { text: "디바이스", style: "tableHeader" },
      { text: "IP 주소", style: "tableHeader" },
      { text: "결과", style: "tableHeader" },
    ],
  ];

  if (rows.length === 0) {
    body.push(["-", "-", "-", "-", "-", "-", "-"]);

    return body;
  }

  rows.forEach((row) => {
    body.push([
      formatDate(row.date),
      formatReportType(row.type),
      formatReportValue(row.userAccount),
      formatReportValue(row.targetPath),
      formatReportValue(row.deviceId),
      formatReportValue(row.remoteIpAddress),
      formatReportValue(row.state),
    ]);
  });

  return body;
};

const downloadBackupReportPdf = async (
  rows: BackupReportItem[],
  dateRange: ReportDateRange,
) => {
  const pdfMake = await preparePdfMakeFonts();

  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [24, 32, 24, 32],
    defaultStyle: {
      font: "GothicA1",
      fontSize: 8,
      color: "#22312f",
    },
    info: {
      title: "SpeedBackup Report",
      subject: `${dateRange.startDate} ~ ${dateRange.endDate}`,
      creator: "SpeedBackup",
    },
    footer: (currentPage: number, pageCount: number) => ({
      text: `${currentPage} / ${pageCount}`,
      alignment: "right",
      margin: [0, 0, 24, 0],
      fontSize: 8,
      color: "#777777",
    }),
    content: [
      {
        text: "백업 리포트",
        style: "title",
      },
      {
        columns: [
          { text: `기간: ${dateRange.startDate} ~ ${dateRange.endDate}` },
          { text: `총 ${rows.length}건`, alignment: "right" },
        ],
        margin: [0, 0, 0, 14],
      },
      {
        table: {
          headerRows: 1,
          widths: [56, 92, 92, "*", 88, 86, 58],
          body: getReportTableBody(rows),
        },
        layout: {
          fillColor: (rowIndex: number) => {
            if (rowIndex === 0) return "#eef8f6";
            return rowIndex % 2 === 0 ? "#fbfdfd" : null;
          },
          hLineColor: () => "#c1e4de",
          vLineColor: () => "#c1e4de",
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
      },
    ],
    styles: {
      title: {
        fontSize: 18,
        bold: true,
        color: "#30a690",
        margin: [0, 0, 0, 8],
      },
      tableHeader: {
        bold: true,
        color: "#0e322b",
      },
    },
  };

  const filename = `speedbackup_report_${dateRange.startDate}_${dateRange.endDate}.pdf`;

  await pdfMake.createPdf(docDefinition).download(filename);
};

const toPdfHex = (value: string) =>
  Array.from(value)
    .map((char) => {
      const codePoint = char.codePointAt(0) || 0;

      if (codePoint <= 0xffff) {
        return codePoint.toString(16).padStart(4, "0");
      }

      const offset = codePoint - 0x10000;
      const high = 0xd800 + (offset >> 10);
      const low = 0xdc00 + (offset & 0x3ff);

      return `${high.toString(16).padStart(4, "0")}${low
        .toString(16)
        .padStart(4, "0")}`;
    })
    .join("")
    .toUpperCase();

const getPdfText = (value: string) => `<${toPdfHex(value)}>`;

const truncatePdfText = (value: string, maxLength: number) => {
  if (value.length <= maxLength) return value;

  return `${value.slice(0, Math.max(maxLength - 1, 0))}…`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const buildBackupReportPdf = (
  rows: BackupReportItem[],
  dateRange: ReportDateRange,
) => {
  const pageWidth = 841.89;
  const pageHeight = 595.28;
  const margin = 36;
  const tableWidth = pageWidth - margin * 2;
  const rowHeight = 24;
  const headerHeight = 26;
  const firstPageTableTop = 490;
  const nextPageTableTop = 520;
  const bottom = 44;
  const columns = [
    { title: "날짜", width: 90, maxLength: 12 },
    { title: "동작", width: 130, maxLength: 10 },
    { title: "사용자 ID", width: 130, maxLength: 20 },
    { title: "경로", width: 170, maxLength: 26 },
    { title: "디바이스", width: 115, maxLength: 18 },
    { title: "IP 주소", width: 125, maxLength: 20 },
    { title: "결과", width: tableWidth - 710, maxLength: 14 },
  ];

  const reportRows =
    rows.length > 0
      ? rows
      : [
          {
            type: "-",
            userAccount: "-",
            targetPath: "-",
            deviceId: "-",
            date: "-",
            remoteIpAddress: "-",
            state: "-",
          },
        ];

  const pages: string[] = [];
  let rowIndex = 0;
  let pageIndex = 0;

  while (rowIndex < reportRows.length) {
    const tableTop = pageIndex === 0 ? firstPageTableTop : nextPageTableTop;
    const rowsPerPage = Math.max(
      Math.floor((tableTop - headerHeight - bottom) / rowHeight),
      1,
    );
    const pageRows = reportRows.slice(rowIndex, rowIndex + rowsPerPage);
    const commands: string[] = [];

    commands.push("q");
    commands.push("0.18 0.65 0.56 rg");
    commands.push(
      `BT /F1 18 Tf ${margin} ${pageHeight - 46} Td ${getPdfText(
        "백업 리포트",
      )} Tj ET`,
    );
    commands.push("0.23 0.23 0.23 rg");
    commands.push(
      `BT /F1 10 Tf ${margin} ${pageHeight - 70} Td ${getPdfText(
        `기간: ${dateRange.startDate} ~ ${dateRange.endDate}`,
      )} Tj ET`,
    );
    commands.push(
      `BT /F1 10 Tf ${margin} ${pageHeight - 86} Td ${getPdfText(
        `총 ${rows.length}건`,
      )} Tj ET`,
    );

    commands.push("0.94 0.98 0.97 rg");
    commands.push(
      `${margin} ${tableTop - headerHeight} ${tableWidth} ${headerHeight} re f`,
    );
    commands.push("0.75 0.89 0.86 RG");
    commands.push("0.7 w");
    commands.push(
      `${margin} ${tableTop - headerHeight} ${tableWidth} ${headerHeight} re S`,
    );

    let x = margin;
    columns.forEach((column) => {
      commands.push(
        `${x} ${tableTop - headerHeight} ${column.width} ${headerHeight} re S`,
      );
      commands.push("0.05 0.2 0.17 rg");
      commands.push(
        `BT /F1 9 Tf ${x + 8} ${tableTop - 17} Td ${getPdfText(
          column.title,
        )} Tj ET`,
      );
      x += column.width;
    });

    pageRows.forEach((row, index) => {
      const y = tableTop - headerHeight - rowHeight * (index + 1);
      const cells = [
        formatDate(row.date),
        formatReportType(row.type),
        formatReportValue(row.userAccount),
        formatReportValue(row.targetPath),
        formatReportValue(row.deviceId),
        formatReportValue(row.remoteIpAddress),
        formatReportValue(row.state),
      ];

      commands.push(index % 2 === 0 ? "1 1 1 rg" : "0.98 0.99 0.99 rg");
      commands.push(`${margin} ${y} ${tableWidth} ${rowHeight} re f`);
      commands.push("0.75 0.89 0.86 RG");

      x = margin;
      cells.forEach((cell, cellIndex) => {
        const column = columns[cellIndex];
        const value = truncatePdfText(cell, column.maxLength);

        commands.push(`${x} ${y} ${column.width} ${rowHeight} re S`);
        commands.push("0.14 0.18 0.17 rg");
        commands.push(
          `BT /F1 8.5 Tf ${x + 8} ${y + 8} Td ${getPdfText(value)} Tj ET`,
        );
        x += column.width;
      });
    });

    commands.push("0.45 0.45 0.45 rg");
    commands.push(
      `BT /F1 8 Tf ${pageWidth - 78} 24 Td ${getPdfText(
        `${pageIndex + 1}`,
      )} Tj ET`,
    );
    commands.push("Q");

    pages.push(commands.join("\n"));
    rowIndex += pageRows.length;
    pageIndex += 1;
  }

  const objects: string[] = [];
  const setObject = (id: number, content: string) => {
    objects[id] = content;
  };
  const addObject = (content: string) => {
    objects.push(content);
    return objects.length - 1;
  };

  setObject(1, "<< /Type /Catalog /Pages 2 0 R >>");
  setObject(2, "");
  setObject(
    3,
    "<< /Type /Font /Subtype /Type0 /BaseFont /HYGoThic-Medium /Encoding /UniKS-UCS2-H /DescendantFonts [4 0 R] >>",
  );
  setObject(
    4,
    "<< /Type /Font /Subtype /CIDFontType0 /BaseFont /HYGoThic-Medium /CIDSystemInfo << /Registry (Adobe) /Ordering (Korea1) /Supplement 2 >> /FontDescriptor 5 0 R >>",
  );
  setObject(
    5,
    "<< /Type /FontDescriptor /FontName /HYGoThic-Medium /Flags 6 /FontBBox [0 -200 1000 900] /ItalicAngle 0 /Ascent 900 /Descent -200 /CapHeight 700 /StemV 80 >>",
  );

  const pageIds = pages.map((content) => {
    const contentId = addObject(
      `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    );

    return addObject(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >>`,
    );
  });

  setObject(
    2,
    `<< /Type /Pages /Kids [${pageIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] /Count ${pageIds.length} >>`,
  );

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += "0000000000 65535 f \n";

  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  }

  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

const HistoryReport = ({
  data,
  pageSize,
  isLoading,
  rangeType,
  dateRange,
  onRangeSelect,
  onCustomDateChange,
}: Props) => {
  const { handleSubmit } = useForm();
  const customDatePickerRef = useRef<HTMLDivElement | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [isCustomCalendarOpen, setIsCustomCalendarOpen] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);

  const columns = useMemo<ColumnDef<BackupReportItem>[]>(
    () => [
      {
        header: "날짜",
        accessorKey: "date",
        cell: (info) => formatDate(info.getValue() as number),
        size: 200,
        enableSorting: true,
      },
      {
        header: "동작",
        accessorKey: "type",
        cell: (info) => {
          const type = String(info.getValue() || "").toLowerCase();
          if (type === "backup") return "백업";
          if (type === "download") return "다운로드";
          return info.getValue() || "-";
        },
        size: 180,
        enableSorting: true,
      },
      {
        header: "사용자 ID",
        accessorKey: "userAccount",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 220,
        enableSorting: true,
      },
      {
        header: "경로",
        accessorKey: "targetPath",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 320,
        enableSorting: true,
      },
      {
        header: "디바이스",
        accessorKey: "deviceId",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 220,
        enableSorting: true,
      },
      {
        header: "IP 주소",
        accessorKey: "remoteIpAddress",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 220,
        enableSorting: true,
      },
      {
        header: "결과",
        accessorKey: "state",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 180,
        enableSorting: true,
      },
    ],
    [data, pagination],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(), // ✅ 이 줄 추가!!
    onPaginationChange: setPagination,
  });

  const totalRowCount = table.getRowCount();

  const onSubmit = async (data: any) => {};
  const selectedStartDate = parseDateParam(dateRange.startDate);
  const selectedEndDate = parseDateParam(dateRange.endDate);
  const calendarValue: [Date, Date] | null =
    selectedStartDate && selectedEndDate
      ? [selectedStartDate, selectedEndDate]
      : null;
  const today = new Date();

  const handleCustomCalendarChange = (value: Value) => {
    if (Array.isArray(value)) {
      const [startDate, endDate] = value;

      if (!startDate || !endDate) return;

      const [rangeStartDate, rangeEndDate] =
        startDate.getTime() <= endDate.getTime()
          ? [startDate, endDate]
          : [endDate, startDate];

      onCustomDateChange("startDate", formatDateParam(rangeStartDate));
      onCustomDateChange("endDate", formatDateParam(rangeEndDate));
      setIsCustomCalendarOpen(false);
      return;
    }

    if (value) {
      onCustomDateChange("startDate", formatDateParam(value));
      onCustomDateChange("endDate", formatDateParam(value));
      setIsCustomCalendarOpen(false);
    }
  };

  const handleRangeSelect = (rangeType: Exclude<ReportRangeType, "custom">) => {
    setIsCustomCalendarOpen(false);
    onRangeSelect(rangeType);
  };

  const handleCustomButtonClick = () => {
    onCustomDateChange("startDate", dateRange.startDate);
    setIsCustomCalendarOpen((prev) => !prev);
  };

  const handlePdfDownload = async () => {
    setIsPdfDownloading(true);

    try {
      await downloadBackupReportPdf(data, dateRange);
    } catch (err) {
      alert("PDF 다운로드에 실패했습니다.");
    } finally {
      setIsPdfDownloading(false);
    }
  };

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (
        customDatePickerRef.current &&
        !customDatePickerRef.current.contains(event.target as Node)
      ) {
        setIsCustomCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, []);

  return (
    <>
      <div className="report-filter-controls">
        <div className="preset-buttons">
          <button
            type="button"
            className={rangeType === "7" ? "active" : ""}
            onClick={() => handleRangeSelect("7")}
          >
            7일
          </button>
          <button
            type="button"
            className={rangeType === "15" ? "active" : ""}
            onClick={() => handleRangeSelect("15")}
          >
            15일
          </button>
          <button
            type="button"
            className={rangeType === "30" ? "active" : ""}
            onClick={() => handleRangeSelect("30")}
          >
            30일
          </button>
          <div className="custom-date-trigger" ref={customDatePickerRef}>
            <button
              type="button"
              className={rangeType === "custom" ? "active" : ""}
              onClick={handleCustomButtonClick}
            >
              사용자 지정
            </button>
            {rangeType === "custom" && isCustomCalendarOpen && (
              <div className="custom-date-popover">
                <Calendar
                  className="custom-date-calendar"
                  locale="ko-KR"
                  maxDate={today}
                  onChange={handleCustomCalendarChange}
                  selectRange
                  value={calendarValue}
                />
              </div>
            )}
          </div>
          {rangeType === "custom" && (
            <div className="custom-date-summary">
              <span>{dateRange.startDate}</span>
              <span>~</span>
              <span>{dateRange.endDate}</span>
            </div>
          )}
        </div>
        <div className="report-actions">
          <button
            type="button"
            className="pdf-download-button"
            onClick={handlePdfDownload}
            disabled={isLoading || isPdfDownloading}
          >
            {isPdfDownloading ? "생성 중..." : "PDF 다운로드"}
          </button>
        </div>
      </div>
      <TableContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="table-head-wrap">
            <div className="left">
              <span className="table-total-row">총 {totalRowCount}</span>
            </div>
          </div>
          {isLoading ? (
            <div className="loading">데이터를 불러오는 중입니다...</div>
          ) : (
            <div className="fixed-height">
              <table>
                <thead>
                  {table.getHeaderGroups().map((item) => (
                    <tr key={item.id}>
                      {item.headers.map((header) => (
                        <th
                          key={header.id}
                          style={{
                            width: `${header.getSize()}px`,
                            cursor: header.column.getCanSort()
                              ? "pointer"
                              : undefined,
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: (
                              <span className="up">
                                <img src="/assets/left-arrow.png" alt="" />
                              </span>
                            ),
                            desc: (
                              <span className="down">
                                <img src="/assets/left-arrow.png" alt="" />
                              </span>
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="no-data-row">
                        🔍 검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </form>

        {table.getPageCount() > 0 && (
          <Pagination
            page={pagination.pageIndex}
            pages={table.getPageCount()}
            onPageChange={(page) => {
              table.setPageIndex(page); // 실제 table 내부 페이지 인덱스 업데이
            }}
          />
        )}
      </TableContainer>
    </>
  );
};

export default HistoryReport;
